import React, { useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { Dimmer, Loader } from 'semantic-ui-react';
import { CarStatus } from '@upstart/harmony/lib/constants';
import validation from '@upstart/harmony/lib/validation';
import { VehicleSelectedAction } from 'util/amplitudeEvents';
import CarService from '../../services/CarService';
import DecodeService from '../../services/DecodeService';
import FloatingInput from '../FloatingInput';
import ErrorMessage from '../ErrorMessage';
import CloseIcon from '../Icons/Close';
import './VINSearch.scss';
import AddVehicleWithoutVin from '../AddVehicleWithoutVin/AddVehicleWithoutVin';

const MODAL_STYLE = {
  content: {
    margin: '0 auto',
    maxWidth: '500px',
    position: 'relative',
  },
};

function VINSearch({
  dealer,
  onClose,
  onSubmit,
  decodedCarCallback,
  onManuallyCreatedCar,
  buttonText,
  allowManualEntry,
  sendAddVehicleAmplitudeEvent,
}) {
  const [vin, setVin] = useState('');
  const [hasError, setHasError] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [decodedCar, setDecodedCar] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);

  const openManualForm = () => {
    setShowManualForm(true);
  };
  const closeManualForm = () => {
    if (sendAddVehicleAmplitudeEvent) {
      sendAddVehicleAmplitudeEvent(
        VehicleSelectedAction.ADDOTHER_MANUALLY_CANCEL,
      );
    }

    setShowManualForm(false);
  };
  const handleManualCreation = car => {
    car.manuallyEnteredSource = 'Vehicle without VIN';
    if (sendAddVehicleAmplitudeEvent) {
      sendAddVehicleAmplitudeEvent(
        VehicleSelectedAction.ADDOTHER_MANUALLY_FINISH,
      );
    }
    onManuallyCreatedCar(car);
  };

  const onFormSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    if (sendAddVehicleAmplitudeEvent) {
      sendAddVehicleAmplitudeEvent(VehicleSelectedAction.ADDOTHER_VIN_START);
    }
    setFetching(true);
    CarService.getCar(vin)
      .then(carCard => {
        // skip sold cars
        if (
          carCard.status === CarStatus.SOLD ||
          carCard.status === CarStatus.MANUALLY_ENTERED
        ) {
          return { isSoldOrManuallyEntered: true, car: carCard };
        }
        onClose();
        onSubmit(carCard);
        return {};
      })
      .catch(async err => {
        // if the car was not found it's not in our db, try to get its details
        if (err && err.status === 404) {
          return {
            isNotInInventory: true,
            car: await DecodeService.decodeVin(vin, dealer?.id),
          };
        }
        // rethrow the error since it couldn't be handled here
        return Promise.reject(err);
      })
      .catch(error =>
        // catch all errors
        ({ isError: true, error }),
      )
      .then(result => {
        setFetching(false);
        if (result.isSoldOrManuallyEntered || result.isNotInInventory) {
          setDecodedCar(result.car);
        } else if (result.isError) {
          setHasError(true);
        }
      });
  };

  // What to render after we have successfully decoded car
  if (decodedCar) {
    return decodedCarCallback(decodedCar);
  }
  const showManual =
    allowManualEntry &&
    !!dealer.tabletFormsConfig?.customerInfo?.useAddCarWithoutVIN;

  return (
    <Modal isOpen style={MODAL_STYLE}>
      <CloseIcon
        onClick={() => {
          onClose();
          if (showManualForm && sendAddVehicleAmplitudeEvent) {
            sendAddVehicleAmplitudeEvent(
              VehicleSelectedAction.ADDOTHER_MANUALLY_CANCEL,
            );
          }
        }}
      />
      {fetching && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
      {showManualForm ? (
        <AddVehicleWithoutVin
          dealer={dealer}
          onClose={closeManualForm}
          onCarCreation={handleManualCreation}
        />
      ) : (
        <form className="vin-search-form" onSubmit={onFormSubmit}>
          <div className="vin-search-header">Please enter a VIN</div>
          {hasError && <ErrorMessage error="Failed to find a car" />}
          <FloatingInput
            label="VIN"
            value={vin}
            onChange={e => setVin(e.target.value)}
            validate={validation.isVinStrict}
            required
          />
          <div className="vin-search-footer">
            {showManual && (
              <button
                type="button"
                onClick={() => {
                  setVin('');
                  openManualForm();
                  if (sendAddVehicleAmplitudeEvent) {
                    sendAddVehicleAmplitudeEvent(
                      VehicleSelectedAction.ADDOTHER_MANUALLY_START,
                    );
                  }
                }}
                className="btn btn-secondary cc-button-outlined capitalize-in-chairman"
              >
                Add manually
              </button>
            )}
            <button type="submit" className="btn btn-primary cc-button-primary">
              {buttonText}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

VINSearch.propTypes = {
  dealer: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  decodedCarCallback: PropTypes.func,
  onManuallyCreatedCar: PropTypes.func,
  allowManualEntry: PropTypes.bool,
  buttonText: PropTypes.string,
  sendAddVehicleAmplitudeEvent: PropTypes.func,
};

export default VINSearch;
